/** Types shared between the Worker API and the React client. */

export const CATEGORIES = ["do", "buy", "go", "try"] as const;
export type Category = (typeof CATEGORIES)[number];

export type IdeaStatus = "open" | "done";

export interface LinkPreview {
  url: string;
  domain: string;
  title?: string;
  image?: string;
}

export interface ChecklistEntry {
  id: string;
  text: string;
  done: boolean;
}

/** AI-proposed content, kept separate from user data until accepted. */
export interface IdeaSuggestions {
  checklist: string[];
}

/** One AI research run, rendered as markdown in its own section. */
export interface ResearchNote {
  /** Markdown. */
  text: string;
  /** Epoch ms of the run. */
  at: number;
}

export interface Idea {
  id: string;
  title: string;
  notes: string;
  category: Category;
  status: IdeaStatus;
  pinned: boolean;
  /** Manual order among pinned items (ascending); 0 for unpinned. */
  sortOrder: number;
  /** ISO date (YYYY-MM-DD) or null. */
  due: string | null;
  links: LinkPreview[];
  checklist: ChecklistEntry[];
  suggestions: IdeaSuggestions | null;
  research: ResearchNote | null;
  /** Epoch ms. */
  createdAt: number;
  updatedAt: number;
  doneAt: number | null;
}

/** POST /api/items body. Everything beyond title is optional. */
export interface IdeaCreate {
  title: string;
  category?: Category;
  notes?: string;
  due?: string | null;
  links?: LinkPreview[];
  checklist?: ChecklistEntry[];
}

/** PATCH /api/items/:id body — any subset of editable fields. */
export interface IdeaPatch {
  title?: string;
  notes?: string;
  category?: Category;
  status?: IdeaStatus;
  pinned?: boolean;
  sortOrder?: number;
  due?: string | null;
  links?: LinkPreview[];
  checklist?: ChecklistEntry[];
  suggestions?: IdeaSuggestions | null;
  /** Clients may only clear research (null); the server writes its content. */
  research?: null;
}

/**
 * POST /api/enrich body. The if* guards make enrichment lose races politely:
 * AI results only apply to fields the user hasn't touched since capture.
 */
export interface EnrichRequest {
  id: string;
  ifCategory?: Category;
  ifTitle?: string;
}

/** POST /api/enrich response. similarTo is a suggestion — merging is the user's call. */
export interface EnrichResponse {
  idea: Idea;
  similarTo: { id: string; title: string } | null;
}
